'use client'

import { useEffect } from 'react'
import { useTranslation } from '@/lib/i18n'
import { readStoredLocale } from '@/lib/i18n/locale-preference'
import { PORTAL_SESSION_EVENT, portalAuthApi } from '@/lib/api/portal-auth'
import { learnerStartLocale } from '@/lib/user-portal/learner-language'

/**
 * Opens the portal in the signed-in learner's language when this device holds
 * no language choice yet (see learnerStartLocale). Re-checked whenever the
 * portal session changes — a sign-in on this tab, or in another one — so a
 * learner who signs in from the login page switches straight away.
 *
 * The language is then saved like a choice from the switcher, which makes this
 * a first-visit rule: from then on the learner's switcher decides.
 */
export function useLearnerLanguage(): void {
  const { setLocale } = useTranslation()

  useEffect(() => {
    const apply = () => {
      const locale = learnerStartLocale({
        recipient: portalAuthApi.getToken() ? portalAuthApi.getRecipient() : null,
        stored: readStoredLocale(),
        isAdminPreview: portalAuthApi.isAdminPreview(),
      })
      if (locale) setLocale(locale)
    }
    apply()
    window.addEventListener(PORTAL_SESSION_EVENT, apply)
    window.addEventListener('storage', apply)
    return () => {
      window.removeEventListener(PORTAL_SESSION_EVENT, apply)
      window.removeEventListener('storage', apply)
    }
  }, [setLocale])
}
